import { isDefined, isNotDefined } from '@sgme/fp';
import { WorkflowErrorDetailsList } from 'components/shared/WorkflowError/WorkflowErrorDetailsList';
import { TradeCancellationModalHeader } from 'components/TradeCancellation/TradeCancellationModal.header';
import type { IntlKey } from 'locales';
import type { FC } from 'react';
import { Modal, ModalBody, ModalFooter } from 'react-bootstrap';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { fromTradeCancellation } from 'state/selectors';
import type { AppState } from 'state/state.model';
import {
  tradeCancellationRequested,
  tradeCancellationWorkflowAborted,
} from 'state/trades/cancellations/cancellations.actions';
import { getSparkUrlByFileCode } from 'state/user';

export const TradeCancellationModal: FC = () => {
  const tradeId = useSelector(fromTradeCancellation.getTradeCancellationTradeId);
  const status = useSelector((state: AppState) => fromTradeCancellation.getTradeCancellationStatus(state, tradeId));
  const blotterType = useSelector((state: AppState) =>
    fromTradeCancellation.getTradeCancellationItemType(state, tradeId),
  );
  const fileCode = useSelector((state: AppState) => fromTradeCancellation.getTradeCancellationFileCode(state, tradeId));
  const isFileCodeExist = useSelector((state: AppState) => fromTradeCancellation.getIsFileCodeExist(state, tradeId));
  const cancellationErrors = useSelector((state: AppState) =>
    fromTradeCancellation.getTradeCancellationErrors(state, tradeId),
  );
  const sparkUrl = useSelector((state: AppState) => getSparkUrlByFileCode(state, fileCode));
  const borderColorClass = useSelector((state: AppState) => fromTradeCancellation.getBorderColorClass(state));

  const dispatch = useDispatch();
  if (isNotDefined(tradeId) || blotterType !== 'trades') {
    return null;
  }

  return (
    <div className="trade-cancellation-modal">
      <Modal show={isDefined(tradeId)}>
        <div className={`shadow-max border ${borderColorClass}`}>
          <TradeCancellationModalHeader tradeId={tradeId} />

          {status === 'INIT' && (
            <>
              <ModalBody className="modal-body fs-16 fw-regular d-flex flex-column gap-3 pb-0">
                {[...Array(3)].map((_, i) => (
                  <p key={i}>
                    <FormattedMessage
                      id={`tradeCancellation.content.INIT.${i + 1}` as IntlKey}
                      values={{ b: txt => <b>{txt}</b> }}
                    />
                  </p>
                ))}
              </ModalBody>
              <ModalFooter>
                <button
                  data-e2e="trade-cancellation-button-keep-this-trade"
                  className="btn btn-lg btn-outline-secondary"
                  type="button"
                  onClick={() => dispatch(tradeCancellationWorkflowAborted(tradeId))}
                >
                  <FormattedMessage id="tradeCancellation.action.INIT.keepThisTrade" />
                </button>
                <button
                  data-e2e="trade-cancellation-button-confirm-cancellation"
                  className="btn btn-lg btn-outline-danger"
                  type="button"
                  onClick={() => dispatch(tradeCancellationRequested(tradeId))}
                >
                  <FormattedMessage id="tradeCancellation.action.INIT.confirmCancellation" />
                </button>
              </ModalFooter>
            </>
          )}

          {status === 'IN_PROGRESS' && (
            <>
              <ModalBody className="modal-body fs-16 fw-regular">
                <p>
                  <FormattedMessage id="tradeCancellation.content.IN_PROGRESS.1" />
                </p>
              </ModalBody>
              <ModalFooter className="bg-danger bg-opacity-10 justify-content-between">
                <div className="d-flex align-items-center justify-content-between">
                  <div className="text-danger">
                    <div className="spinner spinner-sm me-2"></div>
                    <FormattedMessage id="tradeCancellation.content.IN_PROGRESS.2" />
                  </div>
                </div>
              </ModalFooter>
            </>
          )}

          {(status === 'FAILED' || status === 'REJECTED') && (
            <>
              <ModalBody className="modal-body pb-0">
                {cancellationErrors ? (
                  <WorkflowErrorDetailsList
                    shouldDisplayErrorDetails={true}
                    useMaxHeight
                    workflowErrors={cancellationErrors}
                  />
                ) : (
                  <FormattedMessage id="unknownError" />
                )}
              </ModalBody>
              <ModalFooter>
                <button
                  data-e2e="trade-cancellation-button-confirm-cancellation"
                  className="btn btn-lg btn-outline-danger"
                  type="button"
                  onClick={() => dispatch(tradeCancellationWorkflowAborted(tradeId))}
                >
                  <FormattedMessage id="tradeCancellation.action.FAILED.dismiss" />
                </button>
              </ModalFooter>
            </>
          )}

          {status === 'SUCCESS' && isFileCodeExist && (
            <>
              <ModalBody className="modal-body pb-0">
                <FormattedMessage id="tradeCancellation.content.SUCCESS" />
              </ModalBody>
              <ModalFooter>
                <button
                  data-e2e="trade-cancellation-button-dismiss"
                  className="btn btn-lg btn-outline-secondary"
                  type="button"
                  onClick={() => dispatch(tradeCancellationWorkflowAborted(tradeId))}
                >
                  <FormattedMessage id="tradeCancellation.action.SUCCESS.dismiss" />
                </button>
                <a href={sparkUrl} target="_blank" className="btn btn-lg btn-discreet-info">
                  <FormattedMessage id="tradeCancellation.action.SUCCESS.openInSpark" />
                  <em className="icon icon-sm ms-1">open_in_new</em>
                </a>
              </ModalFooter>
            </>
          )}

          {status === 'SUCCESS' && !isFileCodeExist && (
            <>
              <ModalBody className="modal-body">
                <FormattedMessage id="tradeCancellation.content.SUCCESS" />
              </ModalBody>
              <ModalFooter>
                <button
                  data-e2e="trade-cancellation-button-dismiss"
                  className="btn btn-lg btn-outline-secondary"
                  type="button"
                  onClick={() => dispatch(tradeCancellationWorkflowAborted(tradeId))}
                >
                  <FormattedMessage id="tradeCancellation.action.SUCCESS.dismiss" />
                </button>
              </ModalFooter>
            </>
          )}
        </div>
      </Modal>
    </div>
  );
};
